import "./header.css";

import React from "react";
import { useLocation } from "react-router-dom";

import { NAV_LINKS, ROUTES, SECTIONS } from "../../data/content";
import SectionLink from "../general/SectionLink";
import SocialIcons from "../general/SocialIcons";

//Sections in page order, used to highlight the one being viewed
const TRACKED = [SECTIONS.ABOUT, SECTIONS.GALLERY, SECTIONS.CONTACT];

//Works out which section is in view as the visitor scrolls the home page
const useActiveSection = (isHome) => {
  const [active, setActive] = React.useState(SECTIONS.TOP);

  React.useEffect(() => {
    if (!isHome) {
      return undefined;
    }
    const onScroll = () => {
      //A section counts as current once its top passes the upper third of the window
      const line = window.innerHeight / 3;
      let current = SECTIONS.TOP;
      TRACKED.forEach((id) => {
        const el = document.getElementById(id);
        if (el && el.getBoundingClientRect().top <= line) {
          current = id;
        }
      });
      //At the very bottom the contact section may never reach the line
      if (window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 4) {
        current = SECTIONS.CONTACT;
      }
      setActive(current);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [isHome]);

  return isHome ? active : null;
};

const NavItems = ({ active, onNavigate }) => (
  NAV_LINKS.map((link) => (
    <li key={link.label}>
      {link.href ? (
        <a
          href={link.href}
          target={"_blank"}
          rel={"noopener noreferrer"}
          className={"site-nav-link"}
          onClick={onNavigate}
        >
          {link.label}
        </a>
      ) : (
        <SectionLink
          section={link.section}
          className={`site-nav-link ${active === link.section ? "is-active" : ""}`}
          aria-current={active === link.section ? "true" : undefined}
          onClick={onNavigate}
        >
          {link.label}
        </SectionLink>
      )}
    </li>
  ))
);

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const [isScrolled, setIsScrolled] = React.useState(false);
  const { pathname } = useLocation();
  const active = useActiveSection(pathname === ROUTES.HOME);
  const toggleRef = React.useRef(null);
  const closeRef = React.useRef(null);
  const menuRef = React.useRef(null);

  //Slimmer header once the page has scrolled, so it takes less room
  React.useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  //Close the menu after navigating
  React.useEffect(() => {
    setIsMenuOpen(false);
  }, [pathname]);

  React.useEffect(() => {
    if (!isMenuOpen) {
      return undefined;
    }
    //Stop the page behind scrolling, and move focus into the menu
    document.body.style.overflow = "hidden";
    closeRef.current.focus();

    const toggle = toggleRef.current;
    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        setIsMenuOpen(false);
        return;
      }
      //Keep Tab inside the menu while it's open
      if (e.key === "Tab") {
        const focusable = menuRef.current.querySelectorAll("a, button");
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
        else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = "";
      document.removeEventListener("keydown", onKeyDown);
      toggle.focus({ preventScroll: true });
    };
  }, [isMenuOpen]);

  //Unlock scrolling straight away (not after the re-render), so a menu link can scroll to its section
  const closeMenu = () => {
    document.body.style.overflow = "";
    setIsMenuOpen(false);
  };

  return (
    <header className={`site-header ${isScrolled ? "is-scrolled" : ""}`}>
      <div className={"site-header-inner"}>
        <SectionLink
          section={SECTIONS.TOP}
          className={"site-logo"}
          aria-label={"Beechwood Gardens - home"}
        >
          <img
            src={"/images/logo.webp"}
            alt={""}
            width={600}
            height={423}
          />
        </SectionLink>

        <nav
          className={"site-nav"}
          aria-label={"Main"}
        >
          <ul className={"site-nav-list"}>
            <NavItems active={active} />
          </ul>
          <SocialIcons className={"site-nav-social"} />
        </nav>

        <button
          ref={toggleRef}
          type={"button"}
          className={"menu-toggle"}
          aria-expanded={isMenuOpen}
          aria-controls={"site-menu"}
          aria-label={"Open menu"}
          onClick={() => setIsMenuOpen(true)}
        >
          <span aria-hidden={"true"} />
          <span aria-hidden={"true"} />
          <span aria-hidden={"true"} />
        </button>
      </div>

      {/* Full-screen menu on phones & small tablets */}
      <div
        ref={menuRef}
        id={"site-menu"}
        className={"site-menu"}
        role={"dialog"}
        aria-modal={"true"}
        aria-label={"Menu"}
        hidden={!isMenuOpen}
      >
        <button
          ref={closeRef}
          type={"button"}
          className={"menu-close"}
          aria-label={"Close menu"}
          onClick={closeMenu}
        >
          <svg
            viewBox={"0 0 24 24"}
            aria-hidden={"true"}
          >
            <path d={"M5 5l14 14M19 5L5 19"} />
          </svg>
        </button>
        <nav aria-label={"Main"}>
          <ul className={"site-menu-list"}>
            <NavItems
              active={active}
              onNavigate={closeMenu}
            />
          </ul>
        </nav>
        <SocialIcons className={"site-menu-social"} />
      </div>
    </header>
  );
};

export default Header;
